define(["exports", "marked"], function (_exports, _marked) {
  "use strict";

  Object.defineProperty(_exports, "__esModule", {
    value: true
  });
  _exports.markdownToHtml = markdownToHtml;
  _exports.run = run;
  _exports.name = void 0;
  _marked = _interopRequireDefault(_marked);

  function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

  // @ts-check

  /**
   * Module core/markdown
   * Handles the optional markdown processing.
   *
   * Markdown support is optional. It is enabled by setting the `format`
   * property of the configuration object to "markdown."
   *
   * We use marked for parsing Markdown:
   * https://github.com/chjj/marked
   *
   * Note that the content of SECTION elements, and elements with a
   * class name of "note", "issue" or "req" are also parsed.
   *
   * The HTML created by the Markdown parser is turned into a nested
   * structure of SECTION elements, following the structure given by
   * the headings. For example, the following markup:
   *
   *     Title
   *     -----
   *
   *     ### Subtitle ###
   *
   *     Here's some text.
   *
   *     ### Another subtitle ###
   *
   *     More text.
   *
   * will be transformed into:
   *
   *     <section>
   *       <h2>Title</h2>
   *       <section>
   *         <h3>Subtitle</h3>
   *         <p>Here's some text.</p>
   *       </section>
   *       <section>
   *         <h3>Another subtitle</h3>
   *         <p>More text.</p>
   *       </section>
   *     </section>
   *
   * The whitespace of pre elements are left alone.
   */
  const name = "core/markdown";
  _exports.name = name;
  const gtEntity = /&gt;/gm;
  const ampEntity = /&amp;/gm;
  const endsWithSpace = /\s+$/gm;
  const inlineElems = new Set(["a", "abbr", "acronym", "b", "bdo", "big", "br", "button", "cite", "code", "dfn", "em", "i", "img", "input", "kbd", "label", "map", "object", "q", "samp", "script", "select", "small", "span", "strong", "sub", "sup", "textarea", "time", "tt", "var"]);
  /**
   * @param {string} text
   */

  function normalizePadding(text) {
    if (!text) {
      return "";
    }

    if (typeof text !== "string") {
      throw TypeError("Invalid input");
    }

    if (text === "\n") {
      return "\n";
    }
    /**
     * @param {Node} node
     * @return {node is Text}
     */


    function isTextNode(node) {
      return node !== null && node.nodeType === Node.TEXT_NODE;
    }
    /**
     * @param {Node} node
     * @return {node is Element}
     */


    function isElementNode(node) {
      return node !== null && node.nodeType === Node.ELEMENT_NODE;
    }

    const doc = document.createRange().createContextualFragment(text); // Normalize block level elements children first

    Array.from(doc.children).filter(elem => !inlineElems.has(elem.localName)).filter(elem => elem.localName !== "pre").filter(elem => elem.localName !== "table").forEach(elem => {
      elem.innerHTML = normalizePadding(elem.innerHTML);
    }); // Normalize root level now

    Array.from(doc.childNodes).filter(node => isTextNode(node) && node.textContent.trim() === "").forEach(node => node.replaceWith("\n")); // Normalize text node

    if (isElementNode(doc.firstChild)) {
      Array.from(doc.firstChild.children).filter(child => child.localName !== "table").forEach(child => {
        child.innerHTML = normalizePadding(child.innerHTML);
      });
    }

    doc.normalize(); // use the first space as an indicator of how much to chop off the front

    const firstSpace = doc.textContent.replace(/^ *\n/, "").split("\n").filter(item => item && item.startsWith(" "))[0];
    const chop = firstSpace ? firstSpace.match(/ +/)[0].length : 0;

    if (chop) {
      // Chop chop from start, but leave pre elem alone
      Array.from(doc.childNodes).filter(node => node.nodeName !== "PRE").filter(isTextNode).filter(node => {
        // we care about text next to a block level element
        const prevSib = node.previousElementSibling;
        const nextTo = prevSib && prevSib.localName; // and we care about text elements that finish on a new line

        return !inlineElems.has(nextTo) || node.textContent.trim().includes("\n");
      }).reduce((replacer, node) => {
        // We need to retain white space if the text Node is next to an in-line element
        let padding = "";
        const prevSib = node.previousElementSibling;
        const nextTo = prevSib && prevSib.localName;

        if (/^[\t ]/.test(node.textContent) && inlineElems.has(nextTo)) {
          padding = node.textContent.match(/^\s+/)[0];
        }

        node.textContent = padding + node.textContent.replace(replacer, "");
        return replacer;
      }, new RegExp("^ {1,".concat(chop, "}"), "gm")); // deal with pre elements... we can chop whitespace from their siblings

      const endsWithSpace = new RegExp("\\ {".concat(chop, "}$"), "gm");
      Array.from(doc.querySelectorAll("pre")).map(elem => elem.previousSibling).filter(isTextNode).reduce((chop, node) => {
        if (endsWithSpace.test(node.textContent)) {
          node.textContent = node.textContent.substr(0, node.textContent.length - chop);
        }

        return chop;
      }, chop);
    }

    const wrap = document.createElement("body");
    wrap.append(doc);
    const result = endsWithSpace.test(wrap.innerHTML) ? "".concat(wrap.innerHTML.trimRight(), "\n") : wrap.innerHTML;
    return result;
  }
  /**
   * @param {string} text
   */


  function markdownToHtml(text) {
    const normalizedLeftPad = normalizePadding(text); // As markdown is pulled from HTML, > and & are already escaped and
    // so blockquotes aren't picked up by the parser. This fixes it.

    const potentialMarkdown = normalizedLeftPad.replace(gtEntity, ">").replace(ampEntity, "&");
    const result = (0, _marked.default)(potentialMarkdown, {
      sanitize: false,
      gfm: true,
      headerIds: false
    });
    return result;
  }

  function processElements(selector) {
    return element => {
      const elements = Array.from(element.querySelectorAll(selector));
      elements.reverse().forEach(element => {
        element.innerHTML = markdownToHtml(element.innerHTML);
      });
      return elements;
    };
  }

  class Builder {
    constructor(doc) {
      this.doc = doc;
      this.root = doc.createDocumentFragment();
      this.stack = [this.root];
      this.current = this.root;
    }

    findPosition(header) {
      return parseInt(header.tagName.charAt(1), 10);
    }

    findParent(position) {
      let parent;

      while (position > 0) {
        position--;
        parent = this.stack[position];
        if (parent) return parent;
      }
    }

    findHeader({
      firstChild: node
    }) {
      while (node) {
        if (/H[1-6]/.test(node.tagName)) {
          return node;
        }

        node = node.nextSibling;
      }

      return null;
    }

    addHeader(header) {
      const section = this.doc.createElement("section");
      const position = this.findPosition(header);
      section.appendChild(header);
      this.findParent(position).appendChild(section);
      this.stack[position] = section;
      this.stack.length = position + 1;
      this.current = section;
    }

    addSection(node, process) {
      const header = this.findHeader(node);
      const position = header ? this.findPosition(header) : 1;
      const parent = this.findParent(position);

      if (header) {
        node.removeChild(header);
      }

      node.appendChild(process(node));

      if (header) {
        node.prepend(header);
      }

      parent.appendChild(node);
      this.current = parent;
    }

    addElement(node) {
      this.current.appendChild(node);
    }

  }

  function structure(fragment, doc) {
    function process(root) {
      const stack = new Builder(doc);

      while (root.firstChild) {
        const node = root.firstChild;

        if (node.nodeType !== Node.ELEMENT_NODE) {
          root.removeChild(node);
          continue;
        }

        switch (node.localName) {
          case "h1":
          case "h2":
          case "h3":
          case "h4":
          case "h5":
          case "h6":
            stack.addHeader(node);
            break;

          case "section":
            stack.addSection(node, process);
            break;

          default:
            stack.addElement(node);
        }
      }

      return stack.root;
    }

    return process(fragment);
  }
  /**
   * @param {Iterable<Element>} elements
   */


  function substituteWithTextNodes(elements) {
    Array.from(elements).forEach(element => {
      element.replaceWith(element.textContent);
    });
  }

  const processMDSections = processElements("[data-format='markdown']:not(body)");
  const processBlockLevelElements = processElements("[data-format=markdown]:not(body), section, div, address, article, aside, figure, header, main, body");

  function run(conf) {
    const hasMDSections = !!document.querySelector("[data-format=markdown]:not(body)");
    const isMDFormat = conf.format === "markdown";

    if (!isMDFormat && !hasMDSections) {
      return; // Nothing to be done
    } // Only has markdown-format sections


    if (!isMDFormat) {
      processMDSections(document.body).map(elem => {
        const structuredInternals = structure(elem, elem.ownerDocument);
        return {
          structuredInternals,
          elem
        };
      }).forEach(({
        elem,
        structuredInternals
      }) => {
        elem.setAttribute("aria-busy", "true");

        if (structuredInternals.firstElementChild.localName === "section" && elem.localName === "section") {
          const section = structuredInternals.firstElementChild;
          section.remove();
          elem.append(...section.childNodes);
        } else {
          elem.textContent = "";
        }

        elem.appendChild(structuredInternals);
        elem.setAttribute("aria-busy", "false");
      });
      return;
    } // We transplant the UI to do the markdown processing


    const rsUI = document.getElementById("respec-ui");
    rsUI.remove(); // The new body will replace the old body

    const newHTML = document.createElement("html");
    const newBody = document.createElement("body");
    newBody.innerHTML = document.body.innerHTML; // Marked expects markdown be flush against the left margin
    // so we need to normalize the inner text of some block
    // elements.

    newHTML.appendChild(newBody);
    processBlockLevelElements(newHTML); // Process root level text nodes

    const cleanHTML = newBody.innerHTML // Markdown parsing sometimes inserts empty p tags
    .replace(/<p>\s*<\/p>/gm, "");
    newBody.innerHTML = cleanHTML; // Remove links where class .nolinks

    substituteWithTextNodes(newBody.querySelectorAll(".nolinks a[href]")); // Restructure the document properly

    const fragment = structure(newBody, document); // Frankenstein the whole thing back together

    newBody.appendChild(fragment);
    newBody.prepend(rsUI);
    document.body.replaceWith(newBody);
  }
});
//# sourceMappingURL=markdown.js.map